import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { LinkTreeItem } from '../../../@types/link-tree';
import type { Property } from '../../../@types/vehicle-properties';
import { search as searchAction } from '../../../actions/search';
import Link from '../../../components/link';
import type QueryFilter from '../../../helpers/query-filter';
import { SrOnly } from '../../../styled-components/components/SrOnly';
import { resetViewport } from '../../../utils/ts/viewport';
import { ModalContext } from './properties-modal';

interface PropertyValueProps {
  link?: LinkTreeItem;
  property: Property;
  disblePropertyLinks?: boolean;
  search: (queryFilter: QueryFilter) => void;
}

const resetViewportHeight = () => {
  setTimeout(() => {
    resetViewport();
  }, 800);
};

const PropertyValue = ({
  search,
  link,
  property,
  disblePropertyLinks = false,
}: PropertyValueProps) => {
  const { t } = useTranslation(['glossary', 'validation']);

  if (property.value === null || property.value === undefined) {
    return <div className="data-list-value">{t('unknown', { ns: 'validation' })}</div>;
  }

  if (property.unit) {
    return <div className="data-list-value">{`${property.value} ${property.unit}`}</div>;
  }

  if (link?.queryFilter && !disblePropertyLinks) {
    return (
      <div className="data-list-value">
        <Link
          onClick={() => {
            resetViewportHeight();
            search(link.queryFilter as QueryFilter);
          }}
          to={`/sok${link.queryFilter.toQueryURL()}`}
          data-am-link="thin underline current-color hover-text-dark-lighten"
          title={link?.title}
        >
          {property?.value}
        </Link>
      </div>
    );
  } else if (typeof property.value === 'boolean') {
    if (property.value) {
      return (
        <div className="data-list-value">
          <i className="icon-check no-margin text-ui-success" />
          <SrOnly>{t('yes', { ns: 'glossary' })}</SrOnly>
        </div>
      );
    }

    return (
      <div className="data-list-value">
        <i className="icon-close no-margin text-ui-danger" />
        <SrOnly>{t('no', { ns: 'glossary' })}</SrOnly>
      </div>
    );
  }

  if (disblePropertyLinks) {
    return <div className="data-list-value">{property.value}</div>;
  } else if (typeof property.value === 'boolean') {
    if (property.value) {
      return (
        <div className="data-list-value">
          <i className="icon-check no-margin text-ui-success" />
          <SrOnly>{t('yes', { ns: 'glossary' })}</SrOnly>
        </div>
      );
    }

    return (
      <div className="data-list-value">
        <i className="icon-close no-margin text-ui-danger" />
        <SrOnly>{t('no', { ns: 'glossary' })}</SrOnly>
      </div>
    );
  }
  return <div className="data-list-value">{property.value}</div>;
};

interface PropertyComponentProps {
  property?: Property;
  link?: LinkTreeItem;
  disblePropertyLinks?: boolean;
}

const PropertyComponent = ({ link, property, disblePropertyLinks }: PropertyComponentProps) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const actions = bindActionCreators(
    {
      search: searchAction,
    },
    dispatch,
  );
  const { setDisplayModal, setModalTitle, setModalText } = useContext(ModalContext);

  if (!property) {
    return null;
  }

  return (
    <li className={`data-list-item${typeof property?.value === 'boolean' ? ' is-boolean' : ''}`}>
      <div className="data-list-label">
        {property?.hint ? (
          <>
            <button
              type="button"
              title={t('showMoreInformationAboutCategory', {
                ns: 'common',
                category: property.name,
              })}
              className="data-list-btn"
              onClick={() => {
                setModalTitle(property.name);
                setModalText(property.hint || '');
                setDisplayModal(true);
              }}
            >
              {property.name}
            </button>
          </>
        ) : (
          property?.name
        )}
      </div>
      <PropertyValue
        link={link}
        search={actions.search}
        property={property}
        disblePropertyLinks={disblePropertyLinks}
      />
    </li>
  );
};

export default PropertyComponent;
